import { useRef } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useHydrateAtoms } from 'jotai/utils'
import {
  IconPlus,
  InputSelectControl,
  Button,
  Flex
} from '@sequencehq/core-components'
import { useLoadAllItemsQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { LineItemsTable } from 'Invoices/pages/CreateInvoicePage/Editor/components'
import { lineItemEditorConfig, showAddLineItemGroupDropdownAtom } from './atoms'
import { desiredPositionRefAtom } from '../LineItemEditor'
import { NARROW_INPUT_WIDTH } from 'Invoices/components/CreateEditShared/constants'
import {
  isSavePendingAtom,
  lineItemGroupsCountAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { ProtectedButton } from 'lib/rbac'

type Props = {
  showDropdownInitially: boolean
}

export const DropdownAddLineItemGroup = ({ showDropdownInitially }: Props) => {
  useHydrateAtoms([[showAddLineItemGroupDropdownAtom, showDropdownInitially]])

  const [showDropdown, setShowDropdown] = useAtom(
    showAddLineItemGroupDropdownAtom
  )

  const setLineItemEditor = useSetAtom(lineItemEditorConfig)
  const setDesiredPositionRef = useSetAtom(desiredPositionRefAtom)
  const ref = useRef<HTMLDivElement>(null)

  const { data, hasNextPage, isLoading } = useLoadAllItemsQuery(
    dashboardv99990101Client.getProducts,
    { limit: 100 }
  )

  const products = data?.pages?.flatMap(page => page.items) ?? []

  const options = products
    .map(product => ({
      label: product.name,
      value: product.id
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    )

  const handleCreate = ({
    defaultTitle,
    productId,
    defaultTaxCategoryId
  }: {
    defaultTitle?: string
    productId?: string
    defaultTaxCategoryId?: string
  } = {}) => {
    if (!ref.current) return

    setDesiredPositionRef(ref)
    setLineItemEditor({
      variant: 'CREATE_LINE_ITEM_GROUP',
      defaultTitle,
      productId,
      ...(defaultTaxCategoryId ? { defaultTaxCategoryId } : {})
    })

    setShowDropdown(false)
  }

  const positioner = <div ref={ref} style={{ position: 'relative', top: 3 }} />

  if (!showDropdown) {
    return (
      <>
        {positioner}
        <ButtonAddLineItem onClick={() => setShowDropdown(true)} />
      </>
    )
  }

  return (
    <>
      {positioner}
      <LineItemsTable.Row
        hideBorder
        name={
          <Flex marginY="4px" marginRight="16px" flex="1">
            <InputSelectControl
              initiallyOpen
              value=""
              data-testid="addLineItemGroup"
              placeholder="Find or add an item"
              options={options}
              onChange={productId => {
                const product = products.find(({ id }) => productId === id)

                if (!product) return
                handleCreate({
                  defaultTitle: product.name,
                  productId: product.id,
                  defaultTaxCategoryId: product.taxCategoryId
                })
              }}
              onCreate={() => handleCreate()}
              createLabel="Add a new line item"
              triggerStyle={{ width: NARROW_INPUT_WIDTH }}
              disabled={isLoading || hasNextPage}
            />
          </Flex>
        }
        qty={<LineItemsTable.Cell.Skeleton />}
        rate={<LineItemsTable.Cell.Skeleton />}
        tax={<LineItemsTable.Cell.Skeleton />}
        amount={<LineItemsTable.Cell.Skeleton />}
        action={true}
      />
    </>
  )
}

const ButtonAddLineItem = ({ onClick }: { onClick: () => void }) => {
  const groupsCount = useAtomValue(lineItemGroupsCountAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)

  const skeletonColumns =
    groupsCount === 0
      ? {
          qty: <LineItemsTable.Cell.Skeleton />,
          rate: <LineItemsTable.Cell.Skeleton />,
          tax: <LineItemsTable.Cell.Skeleton />,
          amount: <LineItemsTable.Cell.Skeleton />
        }
      : {}

  return (
    <LineItemsTable.Row
      hideBorder
      name={
        <ProtectedButton permission="org.invoice:update">
          <Button
            variant="secondary"
            leadingIcon={<IconPlus color="inherit" />}
            onClick={onClick}
            keyboardShortcut={['Mod', 'L']}
            disabled={isSavePending}
          >
            Add line item
          </Button>
        </ProtectedButton>
      }
      {...skeletonColumns}
      action={true}
      style={{ marginTop: '4px', marginBottom: '4px' }}
    />
  )
}
